import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { ContactCounts } from "@/types/sms";

const CONTACT_COUNTS_QUERY_KEY = ["sms-contact-counts"];

const EMPTY_COUNTS: Omit<ContactCounts, "store_id"> = {
  total_customers: 0,
  total_subscribers: 0,
  total_opt_outs: 0,
  total_invitable: 0,
};

/**
 * Reads the signed-in store's contact tile counts (CT-3) from
 * v_sms_contact_counts (customer-based since PR 0.2b) in one round trip. RLS
 * scopes the select to the caller's own store_id, so a plain
 * select().maybeSingle() is enough — there is at most one row per store. The
 * view has no row until the store has at least one valid-mobile customer, so
 * a missing row reports all-zero counts rather than an error.
 */
export const useContactCounts = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<ContactCounts>({
    queryKey: CONTACT_COUNTS_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb.from("v_sms_contact_counts").select("*").maybeSingle();
      if (error) throw error;
      if (!data) return { store_id: "", ...EMPTY_COUNTS };
      return data as ContactCounts;
    },
    staleTime: 5 * 60 * 1000,
  });
};
